import api from "../services/authService";

// Personal storage areas ("My Files") — one private folder tree per user.
// Mirrors the shape of service/matter.js and service/uploadService.js so the
// two systems stay easy to compare, but every endpoint here is scoped to the
// signed-in user server-side; there is no matter_id concept.
// Backend: documents/views/personal.py, documents/services/personal_storage.py.

export const fetchPersonalHierarchy = () => api.get("/personal/folders/");

export const createPersonalFolder = ({ name, parent_id }) =>
  api.post("/personal/folders/create/", { name, parent_id });

export const renamePersonalFolder = (id, name) =>
  api.post(`/personal/folders/${id}/rename/`, { name });

export const deletePersonalFolder = (id) =>
  api.delete(`/personal/folders/${id}/`);

// Uploads proxy the full file through Django to S3 (no presigned direct-to-S3
// path here, unlike matter uploads), so a slow connection is racing this
// timeout for the whole transfer, not just a round trip. 15 minutes matches
// the backend's own gunicorn worker timeout on staging — no point waiting
// longer client-side than the server will. Callers can still pass a longer
// `timeout` via config if needed.
const UPLOAD_TIMEOUT_MS = 15 * 60 * 1000;

export const uploadPersonalDocument = (formData, { headers, ...config } = {}) =>
  api.post("/personal/documents/upload/", formData, {
    timeout: UPLOAD_TIMEOUT_MS,
    ...config,
    headers: { "Content-Type": "multipart/form-data", ...headers },
  });

export const renamePersonalDocument = (id, name) =>
  api.post(`/personal/documents/${id}/rename/`, { name });

export const movePersonalDocument = (id, folderId) =>
  api.post(`/personal/documents/${id}/move/`, { folder_id: folderId ?? null });

export const deletePersonalDocument = (id) =>
  api.delete(`/personal/documents/${id}/`);

// Word/Excel editing goes through a copy in the owner's own OneDrive; the
// first open makes the copy, sync brings saved changes back into My Files.
export const openPersonalDocumentInWord = (id) =>
  api.post(`/personal/documents/${id}/word/`);

export const syncPersonalDocumentFromWord = (id) =>
  api.post(`/personal/documents/${id}/word/sync/`);

export const createPersonalLink = (documentId) =>
  api.post("/personal/links/create/", { document_id: documentId });

export const revokePersonalLink = (token) =>
  api.post(`/personal/links/${token}/revoke/`);

// Unauthenticated-safe path (still requires login, but not ownership of the
// document) — whoever the link was handed to.
export const fetchPersonalLink = (token) => api.get(`/personal/links/${token}/`);

export const saveCopyFromPersonalLink = (token, payload) =>
  api.post(`/personal/links/${token}/save-copy/`, payload);

export const fetchPersonalStorageDashboard = () => api.get("/personal/dashboard/");
